import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'
import { RESOURCES } from './resources'

const STEPS = [
  [
    'categories',
    'Add categories with images',
    'Create each category (for example Charms) and upload its picture.',
  ],
  [
    'brands',
    'Add phone brands and models',
    'Add each phone brand (for example Apple) and the models it makes, so products can be matched to phones.',
  ],
  [
    'products',
    'Add products with prices',
    'Pick a category, enter the price and upload the product photo. You can change a price later straight from the list.',
  ],
]

function useNow() {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000)
    return () => clearInterval(timer)
  }, [])

  return now
}

export default function AdminDashboard() {
  const now = useNow()
  const hour = now.getHours()

  const greeting =
    hour < 12
      ? 'Good morning'
      : hour < 17
        ? 'Good afternoon'
        : 'Good evening'

  const [counts, setCounts] = useState({})
  const [orders, setOrders] = useState(null) // { total, fresh }

  useEffect(() => {
    api.list('orders', true)
      .then((r) => {
        const list = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.items) ? r.items : []
        setOrders({
          total: list.length,
          fresh: list.filter((o) => !o.status || String(o.status).toLowerCase() === 'new').length,
        })
      })
      .catch(() => setOrders({ total: '–', fresh: '–' }))
  }, [])

  useEffect(() => {
    let cancelled = false

    Object.keys(RESOURCES).forEach((name) => {
      api
        .list(name, true)
        .then((response) => {
          let items = null

          if (Array.isArray(response)) {
            items = response
          } else if (Array.isArray(response?.data)) {
            items = response.data
          } else if (Array.isArray(response?.items)) {
            items = response.items
          }

          if (!cancelled) {
            setCounts((current) => ({
              ...current,
              [name]: items !== null ? items.length : '–',
            }))
          }
        })
        .catch((error) => {
          console.error(`Failed to load ${name}:`, error)

          if (!cancelled) {
            setCounts((current) => ({
              ...current,
              [name]: '–',
            }))
          }
        })
    })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <>
      <header className="adm__greet">
        <div>
          <h1>{greeting}</h1>
          <p className="adm__hint">
            Choose what you want to add or change on the website.
          </p>
        </div>

        <div
          className="adm__clock"
          aria-label="Current date and time"
        >
          <strong>
            {now.toLocaleTimeString('en-IN', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </strong>

          <span>
            {now.toLocaleDateString('en-IN', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
        </div>
      </header>

      <ol className="adm__steps">
        {STEPS.map(([name, title, help], index) => {
          const done = Number(counts[name]) > 0

          return (
            <li
              key={name}
              className={`adm__card adm__step${done ? ' is-done' : ''}`}
            >
              <span className="adm__stepno">
                {done ? '✓' : index + 1}
              </span>

              <div>
                <strong>{title}</strong>
                <div className="adm__hint">{help}</div>
              </div>

              <Link
                className="adm__btn adm__btn--ghost"
                to={`/admin/${name}`}
              >
                {done ? 'Manage' : 'Start'}
              </Link>
            </li>
          )
        })}
      </ol>

      <div className="adm__tiles">
        <Link to="/admin/orders" className="adm__card adm__tile">
          <span className="adm__count">{orders ? orders.fresh : '…'}</span>
          <strong>New orders</strong>
          <span className="adm__hint">{orders ? `${orders.total} in total` : 'Loading'}</span>
        </Link>

        {Object.entries(RESOURCES).map(([name, resource]) => (
          <Link
            key={name}
            to={`/admin/${name}`}
            className="adm__card adm__tile"
          >
            <span className="adm__count">
              {counts[name] ?? '…'}
            </span>

            <strong>{resource.label}</strong>

            <span className="adm__hint">Add or edit</span>
          </Link>
        ))}
      </div>
    </>
  )
}